// src/utils/UrlDataHandler.js

import { trackCampaignDetection } from './Tracking';

// Key for storing campaign data in sessionStorage
const SESSION_STORAGE_KEY = 'campaign_data';

/**
 * Extracts all query parameters from the URL and returns them as an object
 * @returns {Object} Object containing all query parameters
 */
export const extractQueryParams = () => {
  const url = window.location.search;
  const params = new URLSearchParams(url);
  const queryParams = {};
  
  for (const [key, value] of params.entries()) {
    queryParams[key] = value;
  }
  
  return queryParams;
};

/**
 * Extracts a path parameter from the URL
 * @param {number} index - The index of the path segment to extract (0-based)
 * @param {string} basePath - The base path to start from (default: '')
 * @returns {string|null} The extracted path parameter or null if not found
 */
export const extractPathParam = (index, basePath = '') => {
  // Get the path without query parameters
  const path = window.location.pathname;
  
  // If basePath is provided, remove it from the beginning
  let processedPath = path;
  if (basePath && path.startsWith(basePath)) {
    processedPath = path.substring(basePath.length);
  }
  
  // Split the path into segments and remove empty segments
  const segments = processedPath.split('/').filter(segment => segment.length > 0);
  
  // Return the segment at the specified index or null if not found
  return segments.length > index ? segments[index] : null;
};

/**
 * Processes and stores URL campaign data in session storage
 * @returns {Object} The processed campaign data
 */
export const processCampaignData = async () => {
  const queryParams = extractQueryParams();
  const hasAdGroup = 'AdGroup' in queryParams;
  const hasCampaign = 'utm_campaign' in queryParams;
  
  if (!hasAdGroup && !hasCampaign) {
    return getStoredCampaignData();
  }
  
  // Build our campaign payload
  const campaignData = {
    adGroup: queryParams.AdGroup || null,
    utmSource: queryParams.utm_source || null,
    utmMedium: queryParams.utm_medium || null,
    utmCampaign: queryParams.utm_campaign || null,
    utmTerm: queryParams.utm_term || null,
    utmContent: queryParams.utm_content || null,
    gclid: queryParams.gclid || null,
    fbclid: queryParams.fbclid || null,
    timestamp: Date.now()
  };
  
  // Persist locally so pages can read it
  storeCampaignData(campaignData);
  
  // Track campaign detection event (this will handle session creation if needed)
  try {
    await trackCampaignDetection({
      ...queryParams,
      landing_page: window.location.pathname,
      campaign_data: campaignData
    });
  } catch (err) {
    console.error('Error tracking campaign detection:', err);
  }
  
  return campaignData;
};

/**
 * Stores campaign data in session storage
 * @param {Object} data - Campaign data to store
 */
export const storeCampaignData = (data) => {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error storing campaign data:', error);
  }
};

/**
 * Retrieves stored campaign data from session storage
 * @returns {Object|null} The stored campaign data or null if none exists
 */
export const getStoredCampaignData = () => {
  try {
    const data = sessionStorage.getItem(SESSION_STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Error retrieving campaign data:', error);
    return null;
  }
};

/**
 * Checks if a specific ad group is active for the current session
 * @param {string} adGroupName - The ad group name to check
 * @returns {boolean} True if the specified ad group is active
 */
export const isAdGroupActive = (adGroupName) => {
  const campaignData = getStoredCampaignData();
  if (!campaignData || !campaignData.adGroup) return false;
  return campaignData.adGroup.toLowerCase() === adGroupName.toLowerCase();
};

/**
 * Gets the active ad group for the current session
 * @returns {string|null} The active ad group or null if none is active
 */
export const getActiveAdGroup = () => {
  const campaignData = getStoredCampaignData();
  return campaignData?.adGroup || null;
};

/**
 * Initializes the URL data handler, processing any campaign data in the URL
 * Should be called early in the application lifecycle
 */
export const initializeUrlDataHandler = async () => {
  return await processCampaignData();
};

export default {
  extractQueryParams,
  extractPathParam,
  processCampaignData,
  getStoredCampaignData,
  isAdGroupActive,
  getActiveAdGroup,
  initializeUrlDataHandler
};